import type { CSSProperties, ReactNode } from "react";

/**
 * Cross-product link into the central tenant admin (/admin/*, served by the
 * Control app on the same site). Always passes the current page URL as
 * `?return=…` so the admin can hand the user back here after a save.
 *
 * The hub honours this param via `useReturnUrl()` in HubDashboard.
 *
 * When invoked from a sub-portal bundle (legionella / tracker), pass
 * `from` so the hub can surface the originating product in analytics
 * / telemetry and tailor the "return" affordance once the user is done.
 */
export type TenantAdminTarget =
  | "users"
  | "branding"
  | "domains"
  | "sso"
  | "ip"
  | "billing"
  | "audit";

export interface TenantAdminLinkProps {
  to: TenantAdminTarget;
  children?: ReactNode;
  /** Originating product — used as a `?from=` query param for telemetry. */
  from?: "legionella" | "tracker" | "hr";
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function TenantAdminLink({
  to,
  children,
  from,
  className,
  style,
  title,
}: TenantAdminLinkProps) {
  // Same site now: the admin lives at /admin on this origin.
  const target = new URL(`/admin/${to}`, window.location.origin);
  target.searchParams.set("return", window.location.href);
  if (from) target.searchParams.set("from", from);
  return (
    <a href={target.toString()} className={className} style={style} title={title}>
      {children ?? "Tenant settings"}
    </a>
  );
}

export default TenantAdminLink;
